// ──────────────────────────────────────────────────
// Constantes compartidas entre AgendaPT y AlumnoApp
// ──────────────────────────────────────────────────

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// WhatsApp de recepción (fallback si el entrenador no tiene número configurado)
export const GYM_WHATSAPP = '56953146098';

export function formatRut(value) {
  const clean = value.replace(/[^0-9kK]/g, '').toUpperCase();
  if (clean.length <= 1) return clean;
  const body = clean.slice(0, -1).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${body}-${clean.slice(-1)}`;
}

export function rutNorm(r) {
  return (r || '').replace(/\./g, '').replace(/-/g, '').toUpperCase();
}

export function validarRut(rut) {
  const clean = rutNorm(rut);
  if (clean.length < 2) return false;
  const body = clean.slice(0, -1);
  const dv   = clean.slice(-1);
  if (!/^\d+$/.test(body)) return false;
  let sum = 0, mul = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i]) * mul;
    mul = mul === 7 ? 2 : mul + 1;
  }
  const expected = 11 - (sum % 11);
  const dvCalc = expected === 11 ? '0' : expected === 10 ? 'K' : String(expected);
  return dv === dvCalc;
}

// Formatea un celular chileno en vivo como "+56 9 1234 5678"
export function formatTelefono(value) {
  let digits = value.replace(/\D/g, '');
  if (digits.startsWith('56')) digits = digits.slice(2);
  digits = digits.slice(0, 9);
  if (!digits) return '';
  let out = `+56 ${digits.slice(0, 1)}`;
  if (digits.length > 1) out += ` ${digits.slice(1, 5)}`;
  if (digits.length > 5) out += ` ${digits.slice(5, 9)}`;
  return out;
}

// Entrenadores — agregar whatsapp de cada uno cuando lo tengas
// Formato: '569XXXXXXXX' sin +, puntos ni espacios. null = usa recepción.
export const ENTRENADORES_DEFAULT = [
  { id: 1, nombre: 'Agustín',    color: '#00C8E8', colorBg: 'rgba(0,200,232,.18)',   whatsapp: null },
  { id: 2, nombre: 'Magdiel',    color: '#00E5A0', colorBg: 'rgba(0,229,160,.18)',   whatsapp: null },
  { id: 3, nombre: 'Mauricio',   color: '#9B51E0', colorBg: 'rgba(155,81,224,.18)',  whatsapp: null },
  { id: 4, nombre: 'Emilio',     color: '#FFC800', colorBg: 'rgba(255,200,0,.15)',   whatsapp: null },
  { id: 5, nombre: 'Juan Pablo', color: '#FF6B6B', colorBg: 'rgba(255,107,107,.18)', whatsapp: null },
];

export const BLOQUES = [
  '06:00','07:00','08:00','09:00','10:00','11:00',
  '12:00','13:00','14:00','15:00','16:00','17:00',
  '18:00','19:00','20:00','21:00',
];

export const DIAS = ['Lunes','Martes','Miércoles','Jueves','Viernes'];

export const PLANES = [
  { id: 'open',   name: 'Open Gym',    price: '$29.990/mes' },
  { id: 'openpt', name: 'Open + PT',   price: '$59.990/mes' },
  { id: 'lunmar', name: 'Lun/Mar',     price: '$19.990/mes' },
  { id: 'diario', name: 'Pase Diario', price: '$3.990'      },
];

export const getDiaActual = () => {
  const d = new Date().getDay();
  return d === 0 || d === 6 ? 'Lunes' : DIAS[d - 1];
};

// ──────────────────────────────────────────────────
// Utilidades de fecha real (reservas usan fecha ISO, no solo nombre de día)
// ──────────────────────────────────────────────────

const DIA_NOMBRES = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];

export function fechaToISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function isoToFecha(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function getDiaNombre(iso) {
  return DIA_NOMBRES[isoToFecha(iso).getDay()];
}

// Hoy si es día hábil; si es fin de semana, el próximo lunes
export function getFechaHoy() {
  const hoy = new Date();
  const dow = hoy.getDay();
  if (dow === 0) hoy.setDate(hoy.getDate() + 1);
  if (dow === 6) hoy.setDate(hoy.getDate() + 2);
  return fechaToISO(hoy);
}

export function sumarDiasHabiles(iso, delta) {
  const f = isoToFecha(iso);
  const step = delta > 0 ? 1 : -1;
  let restante = Math.abs(delta);
  while (restante > 0) {
    f.setDate(f.getDate() + step);
    if (f.getDay() !== 0 && f.getDay() !== 6) restante -= 1;
  }
  return fechaToISO(f);
}

// Lunes a viernes de la semana que contiene la fecha dada
export function getSemanaDe(iso) {
  const f = isoToFecha(iso);
  const dow = f.getDay();
  const lunes = new Date(f);
  lunes.setDate(f.getDate() - (dow === 0 ? 6 : dow - 1));
  const dias = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date(lunes);
    d.setDate(lunes.getDate() + i);
    dias.push(fechaToISO(d));
  }
  return dias;
}

// Próximos N días hábiles (lunes-viernes) a partir de hoy, incluyéndolo
export function getProximosDiasHabiles(n) {
  const dias = [];
  const f = new Date();
  while (dias.length < n) {
    if (f.getDay() !== 0 && f.getDay() !== 6) dias.push(fechaToISO(f));
    f.setDate(f.getDate() + 1);
  }
  return dias;
}

export function formatFechaLarga(iso) {
  const f = isoToFecha(iso);
  const dia = getDiaNombre(iso);
  const mes = f.toLocaleDateString('es-CL', { month: 'long' });
  return `${dia} — ${f.getDate()} de ${mes}`;
}

export function formatFechaCorta(iso) {
  const f = isoToFecha(iso);
  const dia = getDiaNombre(iso).slice(0, 3).toUpperCase();
  return `${dia} ${f.getDate()}`;
}

// ──────────────────────────────────────────────────
// Entrenadores: vienen de Supabase (tabla profesores), normalizados
// al formato que usan AgendaPT y AlumnoApp
// ──────────────────────────────────────────────────

export function hexToRgba(hex, alpha = 0.18) {
  const c = hex.replace('#', '');
  const bigint = parseInt(c, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

export function normalizarProfesor(p) {
  return {
    id: p.id,
    nombre: p.apellido ? `${p.nombre} ${p.apellido}` : p.nombre,
    rut: p.rut || '',
    color: p.color_ui || '#00C8E8',
    colorBg: hexToRgba(p.color_ui || '#00C8E8'),
    whatsapp: p.telefono ? p.telefono.replace(/[^0-9]/g, '') || null : null,
    // Entrenador "de ingreso": visible para alumnos que aún no tienen PT asignado
    intake: !!p.disponible_intake,
  };
}
